import React from 'react';
import { Badge } from '../../components/Badge/Badge';
import { AuditTimestamp } from './AuditTimestamp';
import { formatAuditValue, isEmptyAuditValue } from '../utils/formatAuditValue';
import type { AuditEntryDetailProps } from './AuditEntryDetail.types';
import type { AuditHistoryItem } from '../types/audit.types';

type BadgeColor = 'green' | 'amber' | 'red';

const ACTION_CONFIG: Record<AuditHistoryItem['action'], { color: BadgeColor; label: string }> = {
  Created: { color: 'green', label: 'Creado' },
  Modified: { color: 'amber', label: 'Actualizado' },
  Deleted: { color: 'red', label: 'Eliminado' },
};

const Placeholder: React.FC = () => (
  <div className="flex flex-col items-center justify-center h-full min-h-[200px] text-content-tertiary dark:text-dark-content-tertiary gap-3 p-8">
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
      className="w-10 h-10 text-border-primary dark:text-dark-border-primary"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m5.231 13.481L15 17.25m-4.5-15H5.625c-.621 0-1.125.504-1.125 1.125v16.5c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Zm3.75 11.625a2.625 2.625 0 1 1-5.25 0 2.625 2.625 0 0 1 5.25 0Z"
      />
    </svg>
    <p className="text-sm text-center">Selecciona un registro del historial para ver los detalles</p>
  </div>
);

/**
 * Panel derecho del split view: muestra el diff campo por campo de una entrada de auditoría.
 * Si no hay entrada seleccionada, muestra un placeholder.
 */
export const AuditEntryDetail: React.FC<AuditEntryDetailProps> = ({ entry, className = '' }) => {
  const containerClasses = ['flex flex-col h-full bg-background-secondary dark:bg-dark-bg-primary', className]
    .join(' ')
    .replace(/\s+/g, ' ')
    .trim();

  if (!entry) {
    return (
      <div className={containerClasses}>
        <Placeholder />
      </div>
    );
  }

  const { color, label } = ACTION_CONFIG[entry.action];

  return (
    <div className={containerClasses}>
      {/* Header — badge de acción + actor + fecha, sobre el fondo gris de la sección. */}
      <div className="flex items-center gap-3 px-4 pt-4 pb-3 flex-shrink-0">
        <Badge color={color} label={label} aria-label={`Acción: ${label}`} />
        {/* `min-w-0 flex-1`: dentro de un flex, un hijo no baja de su ancho de contenido, así que
            `truncate` por sí solo no recorta — empuja a los hermanos. Con un nombre largo la fecha
            se salía de la caja en vez de quedarse pegada a la derecha. */}
        <span className="min-w-0 flex-1 text-sm font-medium text-content-primary dark:text-dark-content-primary truncate">
          {entry.actorFullName}
        </span>
        <AuditTimestamp timestamp={entry.timestamp} className="flex-shrink-0" />
      </div>

      {/* Tarjeta blanca redondeada sobre el gris (padding para que se vea la diferencia),
          con la tabla de cambios dentro. Mismo lenguaje visual que la tabla de MasterCrud:
          banda de cabecera #f8f9fc, cabeceras semibold, filas divididas y hover azul. */}
      <div className="flex-1 min-h-0 px-4 pb-4">
        <div className="h-full overflow-y-auto rounded-lg border border-border-primary dark:border-dark-border-primary bg-white dark:bg-dark-bg-primary">
        {entry.details.length === 0 ? (
          <div className="flex items-center justify-center h-32 text-sm text-content-tertiary dark:text-dark-content-tertiary">
            Sin cambios de campos registrados
          </div>
        ) : (
          <table className="w-full text-sm border-collapse">
            <caption className="sr-only">Cambios registrados en esta entrada</caption>
            <thead>
              <tr className="sticky top-0 z-10 bg-[#f8f9fc] dark:bg-dark-bg-secondary border-b border-border-primary dark:border-dark-border-primary">
                <th
                  scope="col"
                  className="px-4 py-2.5 text-left text-sm font-semibold text-content-primary dark:text-dark-content-primary w-1/3"
                >
                  Campo
                </th>
                <th
                  scope="col"
                  className="px-4 py-2.5 text-left text-sm font-semibold text-content-primary dark:text-dark-content-primary w-1/3"
                >
                  Antes
                </th>
                <th
                  scope="col"
                  className="px-4 py-2.5 text-left text-sm font-semibold text-content-primary dark:text-dark-content-primary w-1/3"
                >
                  Después
                </th>
              </tr>
            </thead>
            <tbody>
              {entry.details.map((detail, index) => {
                const oldEmpty = isEmptyAuditValue(detail.oldValue);
                const newEmpty = isEmptyAuditValue(detail.newValue);
                const oldFormatted = formatAuditValue(detail.oldValue);
                const newFormatted = formatAuditValue(detail.newValue);

                return (
                  <tr
                    key={`${detail.fieldName}-${index}`}
                    className="border-b border-border-secondary dark:border-dark-border-primary last:border-0 hover:bg-[#f0f7ff] dark:hover:bg-primary-900/10 transition-colors duration-150"
                  >
                    <td className="px-4 py-2.5 font-medium text-content-primary dark:text-dark-content-primary align-top">
                      {detail.fieldName}
                    </td>
                    <td className="px-4 py-2.5 align-top text-red-600 dark:text-red-400">
                      {oldEmpty ? (
                        <span className="italic text-content-tertiary dark:text-dark-content-tertiary">{oldFormatted}</span>
                      ) : (
                        oldFormatted
                      )}
                    </td>
                    <td className="px-4 py-2.5 align-top text-emerald-600 dark:text-emerald-400">
                      {newEmpty ? (
                        <span className="italic text-content-tertiary dark:text-dark-content-tertiary">{newFormatted}</span>
                      ) : (
                        newFormatted
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
        </div>
      </div>
    </div>
  );
};

AuditEntryDetail.displayName = 'AuditEntryDetail';
